import {
  IconButton,
  TagIcon,
  defaultColorPickerColor,
  isColorPickerColorValue,
  DataTableColumnMenuButton,
  createDataTableColumnVisibility,
  toast,
  type DataTableColumnVisibility,
} from "@carbon6/c6-frontend-design";
import { useCallback, useMemo, type ChangeEvent, type ReactNode, type SyntheticEvent } from "react";
import { useSearchParams } from "react-router";
import {
  createLabel,
  deleteLabel,
  getReviewOptions,
  invalidateLabelCatalog,
  listAllLabels,
  updateLabel,
  type Label,
} from "@/api";
import { canDeleteShared, canModifyLabel, type MePermissions, type UserRole } from "@/app";
import { PaginationFooter, ResourceBrowserLayout } from "@/components/browse";
import {
  FilterBar,
  FilterResultCount,
  labelTokenDisplay,
  type FilterBarChip,
} from "@/components/filter";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import {
  applyUrlPageToSource,
  goToNextPage,
  goToPreviousPage,
  mirrorSourcePageToUrl,
  normalizeOptionalText,
  writePageParam,
} from "@/utils";
import { usePageParam } from "@/app/usePageParam";
import { GnbMenu } from "@/components/base";
import { HatTextModal } from "@/components/modal";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
import styles from "./LabelPage.module.css";
import type { LabelTableRow } from "./LabelTableRow";
import { LabelTableSource } from "./LabelTableSource";
import { LabelCreateDialog } from "./components/LabelCreateDialog";
import { LabelEditDialog } from "./components/LabelEditDialog";
import { LabelSearchField } from "./components/LabelSearchField";
import { labelColumns } from "./labelColumns";
import {
  DEFAULT_LABEL_AXIS,
  LABEL_AXIS_TABS,
  normalizeLabelAxis,
  type LabelAxis,
} from "./labelAxis";
import {
  createLabelSearchCandidates,
  labelTokenValue,
  labelTokensToFilterSelection,
  parseLabelToken,
} from "./labelFilters";
import {
  EMPTY_LABEL_PAGE_FILTERS,
  addLabelPageToken,
  readLabelPageFilters,
  removeLabelPageToken,
  writeLabelPageFilters,
  type LabelPageFilterState,
} from "./labelPageFilters";
import { formatEmptyLabelMessage, resolveErrorMessage } from "./labelFormatters";
import type { LabelFormState, LabelPageViewState } from "./LabelPageTypes";
import { tableStripError } from "@/components/table/tableStateMessages";

const defaultPageSize = 20;

const emptyLabelForm: LabelFormState = {
  key: "",
  value: "",
  color: defaultColorPickerColor,
  description: "",
};

const tabSearchParamKey = "tab";

export function LabelPage() {
  // 조회 축(텍스트 검색, 라벨 조건, 축 탭)은 URL 이 소유한다(rules.md §17). 그래서 새로고침과
  // 딥링크 뒤에도 유지된다.
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, setPage } = usePageParam();
  const filters = useMemo(() => readLabelPageFilters(searchParams), [searchParams]);
  const axis = normalizeLabelAxis(searchParams.get(tabSearchParamKey));
  const handleTabChange = useCallback(
    (next: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        // 기본 탭이면 키를 지워 정리된 URL 이 canonical 이 되게 한다.
        if (next === DEFAULT_LABEL_AXIS) {
          params.delete(tabSearchParamKey);
        } else {
          params.set(tabSearchParamKey, next);
        }
        params.delete("page");
        return params;
      });
    },
    [setSearchParams],
  );
  const handleFiltersChange = useCallback(
    (next: LabelPageFilterState) => {
      setSearchParams((prev) => writePageParam(writeLabelPageFilters(prev, next), 1), {
        replace: true,
      });
    },
    [setSearchParams],
  );
  // Embodiments 와 같은 카탈로그 관리 화면이라 게이트도 같다. 라벨 어휘를 쓰는 데이터셋 필터와
  // 배지는 그대로 member 에게 열려 있다.
  return (
    <LabelPageView
      pageTitle="Labels"
      currentMenu={GnbMenu.Label}
      requiredRoles={["admin"]}
      scrollable
      // 필터 바가 헤더와 본문의 경계를 이미 만든다. 그래서 tabpanel 상단 여백을 겹쳐 두지 않는다.
      bodyFlushTop
      // 축 탭. 카탈로그는 성격이 다른 어휘를 한 표에 담는다. 리뷰가 부여하는 닫힌 어휘(결함)는
      // 운영자가 뜻을 다듬는 대상이다. 그래서 그 어휘를 찾는 탭을 따로 둔다.
      tabs={{
        items: LABEL_AXIS_TABS.map((tab) => ({ value: tab.value, label: tab.label })),
        value: axis,
        onValueChange: handleTabChange,
        ariaLabel: "라벨 축 탭",
      }}
      page={page}
      onPageChange={setPage}
      axis={axis}
      filters={filters}
      onFiltersChange={handleFiltersChange}
    />
  );
}

type LabelPageViewProps = PageBaseProps & {
  page: number;
  onPageChange: (page: number) => void;
  axis: LabelAxis;
  filters: LabelPageFilterState;
  onFiltersChange: (next: LabelPageFilterState) => void;
};

class LabelPageView extends PageBase<LabelPageViewProps, LabelPageViewState> {
  private currentUserRole: UserRole | null = null;
  private currentUserPermissions: MePermissions | null = null;

  private readonly renderRowActions = (label: LabelTableRow): ReactNode => {
    const canModify = canModifyLabel(this.currentUserRole);
    // 삭제는 공유 카탈로그 권한(서버 SoT)으로 제한한다. 등급을 추정하지 않고 /v1/me permissions 를 쓴다.
    const canDelete = canDeleteShared(this.currentUserPermissions, this.currentUserRole);
    const isLoading = this.labelSource.getSnapshot().loadState === "loading";
    // managed 는 "코드가 이 (key, value) 의 **존재**를 보장한다" 는 뜻이다. 그래서 삭제만
    // 막는다(서버 409). 색과 설명은 운영자가 고치는 표시 속성이라 편집할 수 있다. seed 는
    // 그 편집을 덮어쓰지 않는다(BE insert-if-absent).
    const isManaged = label.managed;

    return (
      <div className={styles.rowActions}>
        <IconButton
          text="편집"
          size="sm"
          variant="surface"
          disabled={!canModify || isLoading}
          onClick={() => this.openEditDialog(label)}
        />
        <IconButton
          text="삭제"
          size="sm"
          variant="surface"
          tone="danger"
          title={isManaged ? "코드가 존재를 보장하는 라벨이라 지울 수 없습니다." : undefined}
          disabled={!canDelete || isLoading || isManaged}
          onClick={() => this.openDeleteDialog(label)}
        />
      </div>
    );
  };

  // 첫 조건은 생성 시 소스에 넣어 initialize 한 번으로 처리한다. 그 뒤의 변경은 componentDidUpdate 가 처리한다.
  private readonly labelSource = new LabelTableSource({
    pageSize: defaultPageSize,
    initialPage: this.props.page,
    rowActionsBuilder: this.renderRowActions,
    resolveErrorMessage,
    initialSearchQuery: this.props.filters.search,
    initialFilterSelection: labelTokensToFilterSelection(this.props.filters.tokens),
    initialAxis: this.props.axis,
  });

  state: LabelPageViewState = {
    dialog: { kind: "closed" },
    labelCatalogErrorMessage: null,
    // 카탈로그를 받기 전은 대기다. `initPage` 가 리뷰 어휘를 먼저 기다리므로 그동안도 대기로 표시한다.
    labelCatalogLoading: true,
    labelCandidates: [],
    tableColumnVisibility: createDataTableColumnVisibility(labelColumns),
  };

  private disposed = false;

  protected override async initPage(): Promise<void> {
    // 페이지 진입 시 source 변경을 class component render 흐름에 연결한다.
    this.disposed = false;
    this.labelSource.subscribe(() => {
      if (!this.disposed) {
        // URL 조건을 적용하는 동안 setter 가 설정하는 page=1 을 URL 에 다시 쓰지 않는다.
        if (this.labelSource.getSnapshot().loadState !== "loading") {
          mirrorSourcePageToUrl(this.labelSource, this.props.page, this.props.onPageChange);
        }
        this.forceUpdate();
      }
    });
    // 리뷰 어휘 계약을 **먼저** 받는다. 행을 만들 때 `쓰이는 곳` 을 함께 계산하기 때문이다.
    // 세션 캐시 + 앱 시작 prefetch 라 보통 즉시 끝난다. 실패해도 degrade 하므로 목록을 막지 않는다.
    await getReviewOptions();
    if (this.disposed) {
      return;
    }
    await Promise.all([this.labelSource.initialize(), this.loadLabelCatalog()]);
  }

  protected override disposePage(): void {
    // 페이지를 떠날 때 source lifecycle 을 종료해 listener 와 pending request 를 정리한다.
    this.disposed = true;
    this.labelSource.dispose();
  }

  // URL 조건이 바뀌면(필터 바, 칩, 탭, 뒤로가기) 소스에 전달한다. setter 는 같은 값이면 no-op 이다.
  componentDidUpdate(prevProps: LabelPageViewProps): void {
    const { axis, filters } = this.props;
    if (axis !== prevProps.axis) {
      void this.labelSource.setAxis(axis);
    }
    if (filters.search !== prevProps.filters.search) {
      void this.labelSource.setSearchQuery(filters.search);
    }
    if (filters.tokens !== prevProps.filters.tokens) {
      void this.labelSource.setFilterSelection(labelTokensToFilterSelection(filters.tokens));
    }
    if (
      this.props.page !== prevProps.page ||
      axis !== prevProps.axis ||
      filters !== prevProps.filters
    ) {
      applyUrlPageToSource(this.labelSource, this.props.page);
    }
  }

  // 제목 줄 아래 필터 바. 검색 콤보박스(텍스트 검색 + 라벨 조건 후보)와 적용 조건 칩을 둔다.
  protected override renderBelowTabs(): ReactNode {
    const { filters, onFiltersChange } = this.props;
    const { labelCandidates, labelCatalogErrorMessage, labelCatalogLoading } = this.state;
    const appliedValues = new Set(filters.tokens.map(labelTokenValue));
    const chips: FilterBarChip[] = filters.tokens.map((token) => {
      const value = labelTokenValue(token);
      const label = labelTokenDisplay(token.key, token.value);
      return {
        key: `label:${value}`,
        label,
        removeLabel: `라벨 조건 ${label} 해제`,
        onRemove: () => onFiltersChange(removeLabelPageToken(filters, value)),
      };
    });
    if (filters.search !== "") {
      chips.push({
        key: "search",
        label: `검색: ${filters.search}`,
        removeLabel: `검색 ${filters.search} 해제`,
        onRemove: () => onFiltersChange({ ...filters, search: "" }),
      });
    }
    return (
      <FilterBar
        ariaLabel="라벨 필터"
        search={
          <LabelSearchField
            search={filters.search}
            candidates={labelCandidates}
            appliedValues={appliedValues}
            loading={labelCatalogLoading && labelCandidates.length === 0}
            errorMessage={
              labelCatalogErrorMessage !== null && labelCandidates.length === 0
                ? "라벨 조건 목록을 불러오지 못했습니다. 검색어 검색은 그대로 쓸 수 있습니다."
                : undefined
            }
            onSearchCommit={this.handleSearchCommit}
            onSelectCandidate={this.handleSelectCandidate}
          />
        }
        chips={chips}
        onClearAll={this.handleClearAllFilters}
      />
    );
  }

  // 제목 오른쪽의 새 라벨 버튼. 목록 조건과 관계없는 페이지 단위 생성 진입점이라 헤더에 둔다.
  protected override renderHeaderActions(context: PageBaseContext): ReactNode {
    const canModify = canModifyLabel(context.currentUserRole);
    const isLoading = this.labelSource.getSnapshot().loadState === "loading";
    return (
      <IconButton
        text="새 라벨"
        variant="solid"
        disabled={!canModify || isLoading}
        onClick={this.openCreateDialog}
      >
        <TagIcon />
      </IconButton>
    );
  }

  protected override renderContent({
    currentUserRole,
    currentUserPermissions,
  }: PageBaseContext): ReactNode {
    this.currentUserRole = currentUserRole;
    this.currentUserPermissions = currentUserPermissions;

    const { dialog, labelCatalogErrorMessage, tableColumnVisibility } = this.state;
    const { filters } = this.props;
    const sourceSnapshot = this.labelSource.getSnapshot();
    const { loadState, page, size, total } = sourceSnapshot;
    const isLoading = loadState === "loading";
    const canModify = canModifyLabel(currentUserRole);
    const emptyMessage = formatEmptyLabelMessage({
      hasSearchQuery: filters.search !== "",
      searchQuery: filters.search,
      hasFilterSelection: filters.tokens.length > 0,
    });

    return (
      <>
        <ResourceBrowserLayout
          ariaLabel="라벨 목록"
          summaryCount={total}
          summaryLabel="라벨"
          summaryInFilter
          notice={canModify ? null : "Label 생성, 수정, 삭제는 admin 권한에서만 가능합니다."}
          // 목록 오류와 카탈로그(후보) 오류는 따로 발생한다. 둘 다 같은 자리에 알린다.
          error={tableStripError(sourceSnapshot) ?? labelCatalogErrorMessage}
          // 목록과 카탈로그를 함께 다시 받는다. 어느 쪽이 실패했든 같은 버튼으로 다시 읽는다.
          onRetry={this.handleRefreshClick}
          footer={
            <PaginationFooter
              page={page}
              size={size}
              total={total}
              isLoading={isLoading}
              ariaLabel="라벨 페이지 이동"
              onPrevious={this.handlePreviousPageClick}
              onNext={this.handleNextPageClick}
              trailingAction={
                <DataTableColumnMenuButton
                  columns={labelColumns}
                  columnVisibility={tableColumnVisibility}
                  menuPlacement="top"
                  onColumnVisibilityChange={this.handleTableColumnVisibilityChange}
                />
              }
            />
          }
        >
          {/* 목록 상단 띠. 결과 수(idle)와 새로고침을 둔다. 행 선택이 없는 목록이라 액션은 없다. */}
          <div className={bulkActionBarStyles.host}>
            <BulkActionBar
              source={this.labelSource}
              idleContent={<FilterResultCount count={total} unitLabel="라벨" />}
              rightContent={
                <IconButton
                  size="sm"
                  text={isLoading ? "로딩 중…" : "새로고침"}
                  variant="surface"
                  disabled={isLoading}
                  onClick={this.handleRefreshClick}
                />
              }
              ariaLabel="라벨 목록 도구"
            />
            <HatDataTable
              source={this.labelSource}
              columns={labelColumns}
              columnVisibility={tableColumnVisibility}
              emptyMessage={emptyMessage}
              loadingMessage="라벨을 불러오고 있습니다."
              ariaLabel="라벨 목록"
              scrollable
              minWidth="980px"
            />
          </div>
        </ResourceBrowserLayout>

        {dialog.kind === "create" ? (
          <LabelCreateDialog
            dialog={dialog}
            onClose={this.closeDialog}
            onColorChange={this.handleCreateColorChange}
            onDescriptionChange={this.handleCreateDescriptionChange}
            onKeyChange={this.handleCreateKeyChange}
            onSubmit={this.handleCreateSubmit}
            onValueChange={this.handleCreateValueChange}
          />
        ) : null}
        {dialog.kind === "edit" ? (
          <LabelEditDialog
            dialog={dialog}
            onClose={this.closeDialog}
            onColorChange={this.handleEditColorChange}
            onDescriptionChange={this.handleEditDescriptionChange}
            onSubmit={this.handleEditSubmit}
          />
        ) : null}
        {dialog.kind === "delete" ? (
          <HatTextModal
            open
            eyebrow={`${dialog.label.key} / ${dialog.label.value}`}
            label="라벨을 삭제합니다."
            caption="이 라벨을 삭제하면 연결된 Dataset 매핑도 함께 삭제됩니다."
            error
            errorMessage={dialog.errorMessage ?? undefined}
            busy={dialog.submitting}
            okText="삭제"
            onOkPress={this.handleDeleteConfirm}
            onCancelPress={this.closeDialog}
          />
        ) : null}
      </>
    );
  }

  private readonly handlePreviousPageClick = () => {
    goToPreviousPage(this.labelSource, this.props.onPageChange);
  };

  private readonly handleNextPageClick = () => {
    goToNextPage(this.labelSource, this.props.onPageChange);
  };

  private async loadLabelCatalog(): Promise<void> {
    this.setState({ labelCatalogLoading: true, labelCatalogErrorMessage: null });

    try {
      const labels = await listAllLabels();
      if (this.disposed) {
        return;
      }

      this.setState({
        labelCatalogErrorMessage: null,
        labelCatalogLoading: false,
        labelCandidates: createLabelSearchCandidates(labels),
      });
    } catch (error) {
      if (this.disposed) {
        return;
      }
      this.setState({
        labelCatalogErrorMessage: resolveErrorMessage(error),
        labelCatalogLoading: false,
      });
    }
  }

  private async refreshLabels(): Promise<void> {
    invalidateLabelCatalog();
    await Promise.all([this.loadLabelCatalog(), this.labelSource.refresh()]);
  }

  private readonly handleRefreshClick = () => {
    void this.refreshLabels();
  };

  private readonly handleSearchCommit = (search: string) => {
    this.props.onFiltersChange({ ...this.props.filters, search });
  };

  // 후보 선택. 조건을 더하고 검색을 함께 비운다. 후보를 찾던 글자가 텍스트 검색으로 남지 않게 한다.
  private readonly handleSelectCandidate = (tokenValue: string) => {
    const token = parseLabelToken(tokenValue);
    if (token === null) {
      return;
    }
    this.props.onFiltersChange(addLabelPageToken({ ...this.props.filters, search: "" }, token));
  };

  private readonly handleClearAllFilters = () => {
    this.props.onFiltersChange(EMPTY_LABEL_PAGE_FILTERS);
  };

  private readonly handleTableColumnVisibilityChange = (
    tableColumnVisibility: DataTableColumnVisibility,
  ) => {
    this.setState({ tableColumnVisibility });
  };

  private readonly openCreateDialog = () => {
    this.setState({
      dialog: {
        kind: "create",
        form: { ...emptyLabelForm },
        errorMessage: null,
        submitting: false,
      },
    });
  };

  private readonly openEditDialog = (label: Label) => {
    this.setState({
      dialog: {
        kind: "edit",
        label,
        form: {
          color: label.color,
          description: label.description ?? "",
        },
        errorMessage: null,
        submitting: false,
      },
    });
  };

  private readonly openDeleteDialog = (label: Label) => {
    this.setState({
      dialog: {
        kind: "delete",
        label,
        errorMessage: null,
        submitting: false,
      },
    });
  };

  private readonly closeDialog = () => {
    if (this.isDialogSubmitting()) {
      return;
    }
    this.setState({ dialog: { kind: "closed" } });
  };

  private readonly handleCreateKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.patchCreateForm({ key: event.target.value });
  };

  private readonly handleCreateValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.patchCreateForm({ value: event.target.value });
  };

  private readonly handleCreateColorChange = (color: number) => {
    this.patchCreateForm({ color });
  };

  private readonly handleCreateDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.patchCreateForm({ description: event.target.value });
  };

  private readonly handleEditColorChange = (color: number) => {
    const { dialog } = this.state;
    if (dialog.kind !== "edit") {
      return;
    }
    this.setState({
      dialog: {
        ...dialog,
        form: {
          ...dialog.form,
          color,
        },
        errorMessage: null,
      },
    });
  };

  private readonly handleEditDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { dialog } = this.state;
    if (dialog.kind !== "edit") {
      return;
    }
    this.setState({
      dialog: {
        ...dialog,
        form: {
          ...dialog.form,
          description: event.target.value,
        },
        errorMessage: null,
      },
    });
  };

  private readonly handleCreateSubmit = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    event.preventDefault();
    void this.submitCreate();
  };

  private readonly handleEditSubmit = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    event.preventDefault();
    void this.submitEdit();
  };

  private readonly handleDeleteConfirm = () => {
    void this.submitDelete();
  };

  private patchCreateForm(value: Partial<LabelFormState>): void {
    const { dialog } = this.state;
    if (dialog.kind !== "create") {
      return;
    }
    this.setState({
      dialog: {
        ...dialog,
        form: {
          ...dialog.form,
          ...value,
        },
        errorMessage: null,
      },
    });
  }

  private async submitCreate(): Promise<void> {
    const { dialog } = this.state;
    if (dialog.kind !== "create" || dialog.submitting) {
      return;
    }

    const key = dialog.form.key.trim();
    const value = dialog.form.value.trim();
    const color = dialog.form.color;
    const description = normalizeOptionalText(dialog.form.description);

    if (key === "" || value === "") {
      this.setState({
        dialog: {
          ...dialog,
          errorMessage: "Key와 Value를 입력해 주세요.",
        },
      });
      return;
    }

    if (!isColorPickerColorValue(color)) {
      this.setState({
        dialog: {
          ...dialog,
          errorMessage: "Color는 24-bit RGB 값이어야 합니다.",
        },
      });
      return;
    }

    this.setState({ dialog: { ...dialog, submitting: true, errorMessage: null } });

    try {
      const { created } = await createLabel(
        {
          key,
          value,
          color,
          description,
        },
        { role: this.currentUserRole },
      );
      if (this.disposed) {
        return;
      }
      // 서버는 멱등 get-or-create 라 같은 (key,value) 면 새로 만들지 않고 기존 라벨을 반환한다.
      // 그대로 닫으면 사용자는 자기가 만든 것으로 알고, 색·설명이 자기 입력과 다른 이유를 모른다.
      if (!created) {
        toast.info("같은 key와 값의 라벨이 이미 있어 그 라벨을 그대로 씁니다.");
      }
      this.setState({ dialog: { kind: "closed" } });
      // 카탈로그 캐시 갱신과 테이블 1페이지 이동은 서로 독립이라 병렬로 보낸다(직렬 RTT 제거).
      await Promise.all([this.loadLabelCatalog(), this.labelSource.goToPage(1)]);
    } catch (error) {
      if (this.disposed) {
        return;
      }
      this.setState({
        dialog: {
          ...dialog,
          submitting: false,
          errorMessage: resolveErrorMessage(error),
        },
      });
    }
  }

  private async submitEdit(): Promise<void> {
    const { dialog } = this.state;
    if (dialog.kind !== "edit" || dialog.submitting) {
      return;
    }

    const color = dialog.form.color;
    if (!isColorPickerColorValue(color)) {
      this.setState({
        dialog: {
          ...dialog,
          errorMessage: "Color는 24-bit RGB 값이어야 합니다.",
        },
      });
      return;
    }

    this.setState({ dialog: { ...dialog, submitting: true, errorMessage: null } });

    try {
      const updated = await updateLabel(
        dialog.label.uuid,
        {
          color,
          description: normalizeOptionalText(dialog.form.description),
        },
        { role: this.currentUserRole },
      );
      if (this.disposed) {
        return;
      }
      await this.loadLabelCatalog();
      this.labelSource.replaceRow(this.labelSource.createRow(updated));
      this.setState({ dialog: { kind: "closed" } });
    } catch (error) {
      if (this.disposed) {
        return;
      }
      this.setState({
        dialog: {
          ...dialog,
          submitting: false,
          errorMessage: resolveErrorMessage(error),
        },
      });
    }
  }

  private async submitDelete(): Promise<void> {
    const { dialog } = this.state;
    if (dialog.kind !== "delete" || dialog.submitting) {
      return;
    }

    this.setState({ dialog: { ...dialog, submitting: true, errorMessage: null } });

    try {
      await deleteLabel(dialog.label.uuid, { role: this.currentUserRole });
      if (this.disposed) {
        return;
      }
      this.labelSource.removeRow(dialog.label.uuid);
      this.setState({ dialog: { kind: "closed" } });
      // 카탈로그 캐시 갱신과 테이블 새로고침은 서로 독립이라 병렬로 보낸다(직렬 RTT 제거).
      await Promise.all([this.loadLabelCatalog(), this.labelSource.refresh()]);
    } catch (error) {
      if (this.disposed) {
        return;
      }
      this.setState({
        dialog: {
          ...dialog,
          submitting: false,
          errorMessage: resolveErrorMessage(error),
        },
      });
    }
  }

  private isDialogSubmitting(): boolean {
    const { dialog } = this.state;
    return dialog.kind !== "closed" && dialog.submitting;
  }
}
